import { partition } from 'lodash';
import { useMemo } from 'react';
import { getContext } from '@fiverr-private/fiverr_context';
import { Gql } from '@fiverr-private/dynamic_matching';
import { Values } from '@fiverr-private/dropdown';
import { FreelancerLanguagePreference, ListOption } from '../../../utils/types';
import { getLanguageName } from '../../../utils/utils';

interface UseLanguagesOptionsProps {
  selectedLanguages: FreelancerLanguagePreference[];
  currentSelected: Values;
}

export const useLanguagesOptions = ({ selectedLanguages, currentSelected }: UseLanguagesOptionsProps) => {
  const { locale } = getContext();
  const userLanguageCode = locale.split('-')[0];

  const languagesOptions = useMemo(() => {
    const options = (Object.values(Gql.LanguageCode) as string[])
      .map((language) => {
        const label = getLanguageName(language);
        return label !== language.toLocaleLowerCase() ? { label, value: language } : undefined;
      })
      .filter((language): language is ListOption => Boolean(language))
      .sort((a, b) => a.label.localeCompare(b.label));
    const [userLanguage, restLanguagesOptions] = partition(
      options,
      (language) => language.value.toLocaleLowerCase() === userLanguageCode
    );
    return [...userLanguage, ...restLanguagesOptions];
  }, [userLanguageCode]);

  const mappedLanguages = Object.fromEntries(languagesOptions.map(({ value, label }) => [value, label]));

  const restSelectedLanguages = useMemo(
    () => selectedLanguages.filter((selected) => selected.languageCode !== currentSelected.item),
    [selectedLanguages, currentSelected.item]
  );

  const filteredLanguages = useMemo(
    () =>
      languagesOptions.filter(
        (language) => !restSelectedLanguages.some((selected) => selected.languageCode === language.value)
      ),
    [languagesOptions, restSelectedLanguages]
  );

  return { mappedLanguages, languagesOptions: filteredLanguages };
};
